import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, throwError, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ToastService } from './toast.service';
import { LoggerService } from './logger.service';
import { EmployeeService } from './employee.service';
import {
  Package,
  PackagePayload,
  ApiResponse,
  FilterParams,
  PackageDiscount,
} from '../shared/types';
import { normalizeAddOnPackagesResponse } from '../shared/utils/add-on-packages-response.util';
import type { LocationTunnelWashPackages } from '../shared/types/package-tunnel-wash.types';
import { mapLocationTunnelWashPackages } from '../shared/utils/package-tunnel-wash.util';

/**
 * Service for managing package data, exposing a packages stream, performing CRUD operations, and providing cached loaders for car-wash and detailing packages.
 * @component
 * @selector app-package-service
 * @example
 *   // Inject the service into a component or another service via Angular DI
 *   constructor(private packageService: PackageService) {}
 *
 *   // Subscribe to package updates
 *   this.packageService.packages$.subscribe(pkgs => { ... });
 * @input none none - No input properties; this injectable is providedIn: 'root' and is obtained via dependency injection.
 * @output Observable<Package[]> packages$ - Observable that emits the current list of processed packages and updates when loadPackages, removePackageFromList or cache-clearing methods are invoked.
 *
 * Purpose:
 *   - Centralizes all package-related API interactions (list, add, update, delete).
 *   - Processes raw package responses to add computed properties used by UI components (locations_str, locations_tooltip, features_tooltip, status_str).
 *   - Implements caching strategies for car-wash packages (for orders and for staff) to minimize duplicate API calls and to provide promise-to-observable bridging while loading.
 *   - Handles logging and user-facing messages via injected LoggerService and ToastService.
 *
 * Key methods:
 *   - loadPackages(params?: FilterParams): void
 *       Loads packages for the current user (with optional filters), processes them, and updates packages$ via an internal BehaviorSubject. Clears the table and surfaces friendly messages on error.
 *
 *   - addPackage(packageData: PackagePayload): Observable<ApiResponse<Package>>
 *       Creates a new package via the API. Clears car-wash caches on success. Logs errors and forwards API errors to callers.
 *
 *   - updatePackage(packageId: string, payload: Partial<PackagePayload>): Observable<ApiResponse<Package>>
 *       Updates an existing package, clears relevant caches on success, and notifies users on failure via ToastService.
 *
 *   - deletePackage(packageId: string): Observable<ApiResponse<any>>
 *       Deletes a package, clears relevant caches on success, and reports errors via ToastService.
 *
 *   - removePackageFromList(packageId: string): void
 *       Removes a package from the current packages BehaviorSubject without making an API call (local update for optimistic UI).
 *
 *   - getCarWashPackages(): Observable<ApiResponse<Package[]>>
 *       Returns cached car-wash packages when available. If not cached, fetches from API and caches the result. While loading, returns an observable that waits on the in-progress promise to avoid duplicate requests.
 *
 *   - getCarWashPackagesForStaff(sortBy?: string): Observable<Package[]>
 *       Fetches car-wash packages tailored for staff/employees. Supports mapping frontend sort keys to API sort parameters. When no sort is provided, uses and populates an internal cache; providing a sort forces a fresh fetch.
 *
 *   - getDetailingPackages(): Observable<ApiResponse<Package[]>>
 *       Fetches detailing packages from the API. Errors are logged and propagated; UI components are expected to handle user-facing messages.
 *
 *   - getPackagesByLocation(locationId: string): Observable<Package[]>
 *       Fetches packages scoped to a location, processes them, and returns processed packages. Shows user-friendly messages on error.
 *
 *   - getPackagesForCurrentEmployeeLocation(): Observable<Package[]>
 *       Helper that reads the current employee profile to obtain location_id and then delegates to getPackagesByLocation. Returns an empty array on errors or missing location.
 *
 *   - clearPackagesCache(): void
 *       Clears in-memory package streams and all package-related caches (useful when the authenticated user changes).
 *
 *   - clearCarWashPackagesCache(): void
 *       Clears only the car-wash package caches (used after add/update/delete to ensure consumers fetch fresh data).
 *       Also emits on carWashPackagesCacheCleared$ so Order Request / other consumers can drop stale catalogs.
 *
 *   - carWashPackagesCacheCleared$: Observable<void>
 *       Emits whenever car-wash package caches are cleared after package CRUD (or full cache clear).
 *
 * Internal helpers:
 *   - private mapSortByToApiFormat(sortBy?: string): string | undefined
 *       Maps frontend sort keys to API-recognized sort_by values.
 *
 *   - private processPackages(packages: Package[]): Package[]
 *       Adds computed UI-friendly fields (locations_str, locations_tooltip, features_tooltip, status_str) to raw package objects.
 *
 * Error handling and logging:
 *   - Uses LoggerService for debug/info/warn/error logs across methods.
 *   - Uses ToastService to display user-facing notifications for many error conditions (some read-only fetches intentionally leave UI messaging to callers).
 *
 * Lifecycle / Scope:
 *   - Provided in root (singleton service). There are no Angular component lifecycle hooks; the service lifecycle is tied to the application's injector lifetime.
 */
@Injectable({
  providedIn: 'root',
})
export class PackageService {
  private packagesSubject = new BehaviorSubject<Package[]>([]);
  packages$: Observable<Package[]> = this.packagesSubject.asObservable();

  /** True while `view-all-by-user` (or filtered variant) HTTP is in flight. */
  private readonly packagesLoadingSubject = new BehaviorSubject<boolean>(false);
  readonly packagesLoading$ = this.packagesLoadingSubject.asObservable();

  // Cache for car wash packages (used in orders)
  private carWashPackagesCache: Package[] | null = null;
  private carWashPackagesCachePromise: Promise<ApiResponse<Package[]>> | null = null;
  private carWashPackagesForStaffCache: Package[] | null = null;
  private carWashPackagesForStaffCachePromise: Promise<Package[]> | null = null;

  /** Notifies listeners (e.g. Order Request catalog) when package caches are invalidated. */
  private readonly carWashPackagesCacheClearedSubject = new Subject<void>();
  readonly carWashPackagesCacheCleared$ = this.carWashPackagesCacheClearedSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private messageService: ToastService,
    private logger: LoggerService,
    private employeeService: EmployeeService
  ) {}

  /**
   * Loads packages for the current user from the API, applies optional filters, processes results, and updates internal state and UI messages.
   * @example
   * oadPackages({ shopId: '123', page: 1, pageSize: 20 })
   * undefined
   * @param {{FilterParams}} {{params}} - Optional filter and pagination parameters used to query packages.
   * @returns {{void}} Updates internal package subject and displays informational or error messages; does not return a value.
   **/
  loadPackages(params: FilterParams = {}): void {
    let endpoint = 'package/view-all-by-user';
    const query = new URLSearchParams(params as any).toString();
    if (query) {
      endpoint += `?${query}`;
    }

    this.logger.debug('Loading packages', { endpoint, params }, 'PackageService');

    this.packagesLoadingSubject.next(true);

    this.apiService
      .get<{ packages: Package[] }>(endpoint)
      .pipe(
        catchError(err => {
          // Always clear the table when there's an error (including filter results with no data)
          this.packagesSubject.next([]);
          this.logger.error('Failed to load packages', err, 'PackageService');

          if (err.error?.message === 'No packages found for this shop') {
            this.messageService.add({
              severity: 'info',
              summary: 'Info',
              detail: 'No packages found for this shop.',
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: err.error?.message || 'Failed to load packages.',
            });
          }
          return throwError(() => err);
        })
      )
      .subscribe({
        next: (res: ApiResponse<{ packages: Package[] }>) => {
          if (res.status && res.data) {
            const packages = this.processPackages(res.data.packages);
            this.logger.info(
              'Packages loaded successfully',
              { count: packages.length },
              'PackageService'
            );
            this.packagesSubject.next(packages);
          } else {
            this.packagesSubject.next([]);
          }
          this.packagesLoadingSubject.next(false);
        },
        error: () => {
          this.packagesLoadingSubject.next(false);
        },
      });
  }

  /** Synchronous guard for templates / empty state (after synchronous `loadPackages` call). */
  isPackagesLoading(): boolean {
    return this.packagesLoadingSubject.value;
  }

  /**
   * Adds a new package via the API, clears the package cache, and logs progress and errors.
   * @example
   * ddPackage({ name: 'Basic Wash', price: 9.99, duration: 30 })
   * Observable<ApiResponse<Package>>
   * @param {{PackagePayload}} {{packageData}} - Payload containing the package details to be added.
   * @returns {{Observable<ApiResponse<Package>>}} Observable that emits the API response containing the created package.
   **/
  addPackage(packageData: PackagePayload): Observable<ApiResponse<Package>> {
    this.logger.debug('Adding package', packageData, 'PackageService');
    return this.apiService.post<Package>('package/add', packageData).pipe(
      tap(() => {
        // Clear cache when package is added
        this.clearCarWashPackagesCache();
      }),
      catchError(err => {
        this.logger.error('Failed to add package', err, 'PackageService');
        // this.messageService.add({
        //   severity: 'error',
        //   summary: 'Error',
        //   detail: err.error?.message || 'Failed to add package.'
        // });
        return throwError(() => err);
      })
    );
  }

  /**
   * Update a package by its ID with the provided partial payload, clears the local package cache on success, and surfaces errors via the message service on failure.
   * @example
   * pdatePackage('pkg123', { name: 'Basic Wash' })
   * Observable<ApiResponse<Package>> (emits the API response containing the updated Package)
   * @param {{string}} {{packageId}} - The unique identifier of the package to update.
   * @param {{Partial<PackagePayload>}} {{payload}} - Partial payload containing fields to update on the package.
   * @returns {{Observable<ApiResponse<Package>>}} Observable that emits the API response with the updated Package.
   **/
  updatePackage(
    packageId: string,
    payload: Partial<PackagePayload>
  ): Observable<ApiResponse<Package>> {
    this.logger.debug('Updating package', { packageId, payload }, 'PackageService');
    return this.apiService.put<Package>(`package/edit/${packageId}`, payload).pipe(
      tap(() => {
        // Clear cache when package is updated
        this.clearCarWashPackagesCache();
      }),
      catchError(err => {
        this.logger.error('Failed to update package', err, 'PackageService');
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error?.message || 'Failed to update package.',
        });
        return throwError(() => err);
      })
    );
  }

  /**
   * Delete a package by its ID; clears cached packages, logs actions, and displays error messages on failure.
   * @example
   * eletePackage('pkg123')
   * Observable<ApiResponse<any>>
   * @param {{string}} {{packageId}} - The ID of the package to delete.
   * @returns {{Observable<ApiResponse<any>>}} Observable that emits the API response for the delete operation.
   **/
  deletePackage(packageId: string): Observable<ApiResponse<any>> {
    this.logger.debug('Deleting package', { packageId }, 'PackageService');
    return this.apiService.delete(`package/delete/${packageId}`).pipe(
      tap(() => {
        // Clear cache when package is deleted
        this.clearCarWashPackagesCache();
      }),
      catchError(err => {
        this.logger.error('Failed to delete package', err, 'PackageService');
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error?.message || 'Failed to delete package.',
        });
        return throwError(() => err);
      })
    );
  }

  removePackageFromList(packageId: string): void {
    const currentPackages = this.packagesSubject.getValue();
    const updatedPackages = currentPackages.filter(pkg => pkg.id !== packageId);
    this.packagesSubject.next(updatedPackages);
  }

  // New method to fetch car wash packages for orders with caching
  /**
   * Retrieves car wash packages as an Observable, using in-memory cache and a single shared loading promise to avoid duplicate network requests.
   * @example
   * etCarWashPackages()
   * Observable<ApiResponse<Package[]>>
   * @returns {Observable<ApiResponse<Package[]>>} An observable that emits the API response containing an array of Package objects.
   **/
  getCarWashPackages(): Observable<ApiResponse<Package[]>> {
    // Return cached data if available
    if (this.carWashPackagesCache !== null) {
      this.logger.debug(
        'Returning cached car wash packages',
        { count: this.carWashPackagesCache.length },
        'PackageService'
      );
      return of({
        status: true,
        status_code: 200,
        message: 'Packages retrieved successfully',
        data: this.carWashPackagesCache,
      });
    }

    // If already loading, return the existing promise as observable
    if (this.carWashPackagesCachePromise) {
      this.logger.debug(
        'Car wash packages already loading, returning existing promise',
        {},
        'PackageService'
      );
      return new Observable(observer => {
        this.carWashPackagesCachePromise!.then(
          response => observer.next(response),
          error => observer.error(error)
        ).finally(() => observer.complete());
      });
    }

    // Start loading and cache the promise
    this.logger.debug('Fetching car wash packages for orders', {}, 'PackageService');
    this.carWashPackagesCachePromise = new Promise((resolve, reject) => {
      this.apiService
        .get<Package[]>('package/view-car-wash-packages')
        .pipe(
          catchError(err => {
            this.logger.error('Failed to fetch car wash packages', err, 'PackageService');
            this.carWashPackagesCachePromise = null;
            reject(err);
            return throwError(() => err);
          })
        )
        .subscribe({
          next: (response: ApiResponse<Package[]>) => {
            if (response.status && response.data) {
              // Cache the packages
              this.carWashPackagesCache = response.data;
              this.logger.debug(
                'Cached car wash packages',
                { count: response.data.length },
                'PackageService'
              );
            }
            this.carWashPackagesCachePromise = null;
            resolve(response);
          },
          error: err => {
            this.carWashPackagesCachePromise = null;
            reject(err);
          },
        });
    });

    return new Observable(observer => {
      this.carWashPackagesCachePromise!.then(
        response => {
          observer.next(response);
          observer.complete();
        },
        error => {
          observer.error(error);
        }
      );
    });
  }

  // New method to fetch car wash packages for staff/employees with caching
  /**
   * Get car wash packages for staff with optional sorting; uses caching and request deduplication.
   * @example
   * etCarWashPackagesForStaff('price_desc')
   * [{ id: 'pkg1', name: 'Basic Wash', price: 9.99 }]
   * @param {{string}} {{sortBy}} - Optional sort key to order results; when provided forces a fresh API fetch and bypasses cache.
   * @returns {{Observable<Package[]>}} Observable emitting an array of processed Package objects (may be cached when sortBy is not provided).
   **/
  getCarWashPackagesForStaff(sortBy?: string): Observable<Package[]> {
    // Map frontend sort values to API sort values
    const apiSortBy = this.mapSortByToApiFormat(sortBy);

    // Build endpoint with sort parameter if provided
    let endpoint = 'package/car-wash-packages-for-employee';
    if (apiSortBy) {
      endpoint += `?sort_by=${apiSortBy}`;
    }

    // If sort is provided, don't use cache (always fetch fresh data)
    if (sortBy) {
      this.logger.debug(
        'Fetching car wash packages for staff with sort',
        { sortBy, apiSortBy },
        'PackageService'
      );
      return this.apiService.get<Package[]>(endpoint).pipe(
        catchError(err => {
          this.logger.error('Failed to fetch car wash packages for staff', err, 'PackageService');
          return throwError(() => err);
        }),
        switchMap((response: ApiResponse<Package[]>) => {
          if (response.status && response.data) {
            const packages = this.processPackages(response.data);
            this.logger.debug(
              'Fetched car wash packages for staff with sort',
              { count: packages.length, sortBy },
              'PackageService'
            );
            return of(packages);
          }
          this.logger.warn('No car wash packages found for staff', {}, 'PackageService');
          return of([]);
        })
      );
    }

    // Return cached data if available (only when no sort is specified)
    if (this.carWashPackagesForStaffCache !== null) {
      this.logger.debug(
        'Returning cached car wash packages for staff',
        { count: this.carWashPackagesForStaffCache.length },
        'PackageService'
      );
      return of(this.carWashPackagesForStaffCache);
    }

    // If already loading, return the existing promise as observable
    if (this.carWashPackagesForStaffCachePromise) {
      this.logger.debug(
        'Car wash packages for staff already loading, returning existing promise',
        {},
        'PackageService'
      );
      return new Observable(observer => {
        this.carWashPackagesForStaffCachePromise!.then(
          response => observer.next(response),
          error => observer.error(error)
        ).finally(() => observer.complete());
      });
    }

    // Start loading and cache the promise
    this.logger.debug('Fetching car wash packages for staff', {}, 'PackageService');
    this.carWashPackagesForStaffCachePromise = new Promise((resolve, reject) => {
      this.apiService
        .get<Package[]>(endpoint)
        .pipe(
          catchError(err => {
            this.logger.error('Failed to fetch car wash packages for staff', err, 'PackageService');
            this.carWashPackagesForStaffCachePromise = null;
            reject(err);
            return throwError(() => err);
          }),
          switchMap((response: ApiResponse<Package[]>) => {
            if (response.status && response.data) {
              const packages = this.processPackages(response.data);
              // Cache the packages
              this.carWashPackagesForStaffCache = packages;
              this.logger.debug(
                'Cached car wash packages for staff',
                { count: packages.length },
                'PackageService'
              );
              this.carWashPackagesForStaffCachePromise = null;
              resolve(packages);
              return of(packages);
            }
            this.logger.warn('No car wash packages found for staff', {}, 'PackageService');
            const emptyPackages: Package[] = [];
            this.carWashPackagesForStaffCache = emptyPackages;
            this.carWashPackagesForStaffCachePromise = null;
            resolve(emptyPackages);
            return of(emptyPackages);
          })
        )
        .subscribe({
          error: err => {
            this.carWashPackagesForStaffCachePromise = null;
            reject(err);
          },
        });
    });

    return new Observable(observer => {
      this.carWashPackagesForStaffCachePromise!.then(
        response => {
          observer.next(response);
          observer.complete();
        },
        error => {
          observer.error(error);
        }
      );
    });
  }

  /**
   * Maps frontend sort values to API sort format
   * @param sortBy - Frontend sort value
   * @returns API sort value or undefined
   */
  private mapSortByToApiFormat(sortBy?: string): string | undefined {
    if (!sortBy) {
      return undefined;
    }

    const sortMapping: Record<string, string> = {
      'list-price-low-high': 'list_price_low_to_high',
      'list-price-high-low': 'list_price_high_to_low',
      'strikeoff-price-low-high': 'strikeoff_price_low_to_high',
      'strikeoff-price-high-low': 'strikeoff_price_high_to_low',
      'family-plan-low-high': 'family_plan_low_to_high',
      'family-plan-high-low': 'family_plan_high_to_low',
      'recently-updated': 'recently_updated',
      'earliest-updated': 'earliest_updated',
    };

    return sortMapping[sortBy];
  }

  // New method to fetch detailing packages
  getDetailingPackages(): Observable<ApiResponse<Package[]>> {
    this.logger.debug('Fetching detailing packages', {}, 'PackageService');
    return this.apiService.get<Package[]>('package/detailing').pipe(
      catchError(err => {
        this.logger.error('Failed to fetch detailing packages', err, 'PackageService');
        // No toast message - let the component handle the UI
        return throwError(() => err);
      })
    );
  }

  /**
   * Comma-separated `wash_type` filter for Configure Sales Panel / most-used setup (car wash only).
   * Matches GET `package/view-by-location/:id?wash_type=single_wash,monthly_wash,monthly_membership`.
   */
  static readonly SALES_PANEL_VIEW_BY_LOCATION_WASH_TYPES =
    'single_wash,monthly_wash,monthly_membership';

  /**
   * Fetches packages by location ID for sales component
   * @param locationId - The location ID to fetch packages for
   * @param options.washType - Optional comma-separated wash_type query (e.g. single_wash,monthly_wash,monthly_membership)
   * @returns Observable of processed packages
   */
  getPackagesByLocation(
    locationId: string,
    options?: { washType?: string }
  ): Observable<Package[]> {
    const washType = options?.washType?.trim();
    const params: Record<string, string> = {};
    if (washType) {
      params['wash_type'] = washType;
    }
    this.logger.debug('Fetching packages by location', { locationId, washType }, 'PackageService');

    return this.apiService.get<Package[]>(`package/view-by-location/${locationId}`, params).pipe(
      catchError(err => {
        this.logger.error('Failed to fetch packages by location', err, 'PackageService');
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error?.message || 'Failed to fetch packages for this location.',
        });
        return throwError(() => err);
      }),
      switchMap((response: ApiResponse<Package[]>) => {
        if (response.status && response.data) {
          const packages = this.processPackages(response.data);
          this.logger.info(
            'Packages loaded successfully by location',
            { count: packages.length },
            'PackageService'
          );
          return of(packages);
        } else {
          this.logger.warn('No packages found for location', { locationId }, 'PackageService');
          return of([]);
        }
      })
    );
  }

  /**
   * Fetches add-on packages for a location (`GET package/add-ons-by-location/:id`).
   * Used by sales configuration / drag-to-arrange where `view-by-location` is filtered to car wash wash types only.
   */
  getAddOnPackagesByLocation(locationId: string): Observable<Package[]> {
    this.logger.debug('Fetching add-on packages by location', { locationId }, 'PackageService');

    return this.apiService
      .get<Package | Package[]>(`package/add-ons-by-location/${locationId}`)
      .pipe(
        catchError(err => {
          this.logger.error('Failed to fetch add-on packages by location', err, 'PackageService');
          return of({ status: false, data: [] as Package[] } as ApiResponse<Package[]>);
        }),
        switchMap((response: ApiResponse<Package | Package[]>) => {
          if (!response.status || response.data == null) {
            return of([]);
          }
          const rows = (normalizeAddOnPackagesResponse(response.data) as any as Package[]).map(
            p => ({
              ...p,
              type: 'add_on',
            })
          );
          return of(this.processPackages(rows));
        })
      );
  }

  /**
   * Get packages for the current employee's location (staff users).
   * Shared by appointments-table and appointments-filter-modal to avoid duplicating
   * getCurrentEmployeeProfile -> getPackagesByLocation -> catchError logic.
   */
  getPackagesForCurrentEmployeeLocation(): Observable<Package[]> {
    return this.employeeService.getCurrentEmployeeProfile().pipe(
      switchMap((response: { status?: boolean; data?: { location_id?: string } }) => {
        if (response.status && response.data?.location_id) {
          return this.getPackagesByLocation(response.data.location_id);
        }
        this.logger.warn('No location_id in employee profile', undefined, 'PackageService');
        return of([]);
      }),
      catchError(err => {
        this.logger.error('Error loading packages by location', err, 'PackageService');
        return of([]);
      })
    );
  }

  /** Mark/unmark package as default for a location ("most used"). */
  setDefaultPackageForLocation(
    locationId: string,
    packageId: string,
    isDefault: boolean
  ): Observable<ApiResponse<any>> {
    return this.apiService.put<any>(`package/default-for-location/${locationId}`, {
      package_id: packageId,
      is_default: isDefault,
    });
  }

  /**
   * Batch update default packages for a location (single PUT).
   * Body: `{ package_ids: string[], is_default: boolean }` — full list of defaults for the location.
   */
  setDefaultPackagesForLocationBatch(
    locationId: string,
    body: { package_ids: string[]; is_default: boolean }
  ): Observable<ApiResponse<any>> {
    return this.apiService.put<any>(`package/default-for-location/${locationId}`, body);
  }

  /**
   * CWS-1921: tunnel wash packages a car wash package can start, per location
   * (`GET package/tunnel-wash-packages?location_ids=…`). Errors propagate; the package
   * form then shows no tunnel field (nothing is sent, so links stay unchanged).
   */
  getTunnelWashPackages(locationIds: string[]): Observable<LocationTunnelWashPackages[]> {
    if (!locationIds.length) {
      return of([]);
    }
    const query = `location_ids=${locationIds.map(id => encodeURIComponent(id)).join(',')}`;
    return this.apiService.get<unknown>(`package/tunnel-wash-packages?${query}`).pipe(
      map(response => (response?.status ? mapLocationTunnelWashPackages(response.data) : [])),
      catchError(err => {
        this.logger.error('Failed to load tunnel wash packages', err, 'PackageService');
        return throwError(() => err);
      })
    );
  }

  /** Fetch default (most-used) packages configured for a location. */
  getDefaultPackagesByLocation(locationId: string): Observable<ApiResponse<Package[]>> {
    return this.apiService.get<Package[]>(`package/default-by-location/${locationId}`);
  }

  /**
   * Sales configuration list source:
   * GET package/view-all-by-user?page=1&limit=10&sort_by=...&order_by=...
   */
  getSalesConfigPackages(options: {
    page: number;
    limit: number;
    sort_by: string;
    order_by: 'asc' | 'desc';
  }): Observable<Package[]> {
    const params = new URLSearchParams();
    params.set('page', String(options.page));
    params.set('limit', String(options.limit));
    params.set('sort_by', options.sort_by);
    params.set('order_by', options.order_by);
    const endpoint = `package/view-all-by-user?${params.toString()}`;
    return this.apiService.get<any>(endpoint).pipe(
      catchError(err => {
        this.logger.error('Failed to fetch sales config packages', err, 'PackageService');
        return throwError(() => err);
      }),
      switchMap((response: ApiResponse<any>) => {
        if (!response?.status) {
          return of([]);
        }
        const raw = response.data as any;
        const rows: Package[] = Array.isArray(raw)
          ? (raw as Package[])
          : raw != null &&
              typeof raw === 'object' &&
              Array.isArray((raw as { packages?: Package[] }).packages)
            ? (raw as { packages: Package[] }).packages
            : [];
        return of(this.processPackages(rows));
      })
    );
  }

  /**
   * Packages for prepaid wash-name dropdowns.
   * GET package/view-all-by-user?wash_types=single_wash (comma-separated when multiple).
   *
   * `locationIds` and `activeOnly` are optional so existing callers keep the unfiltered
   * behaviour; the discount form passes both so its Wash Package list only offers
   * packages the selected locations actually sell, and only live ones.
   */
  getPackagesByWashTypes(
    washTypes: string,
    options?: { locationIds?: string[]; activeOnly?: boolean }
  ): Observable<Package[]> {
    const normalized = washTypes
      .split(',')
      .map(t => t.trim())
      .filter(Boolean)
      .join(',');
    const locations = (options?.locationIds ?? [])
      .map(id => String(id ?? '').trim())
      .filter(Boolean)
      .join(',');

    const params = new URLSearchParams();
    if (normalized) {
      params.set('wash_types', normalized);
    }
    if (locations) {
      params.set('locations', locations);
    }
    if (options?.activeOnly) {
      params.set('package_status', 'true');
    }
    const queryString = params.toString();
    const endpoint = queryString
      ? `package/view-all-by-user?${queryString}`
      : 'package/view-all-by-user';

    this.logger.debug(
      'Loading packages by wash types',
      { washTypes: normalized, locations, activeOnly: Boolean(options?.activeOnly), endpoint },
      'PackageService'
    );

    return this.apiService.get<any>(endpoint).pipe(
      switchMap((response: ApiResponse<any>) => {
        if (!response?.status) {
          return of([]);
        }
        const raw = response.data as any;
        const rows: Package[] = Array.isArray(raw)
          ? (raw as Package[])
          : raw != null &&
              typeof raw === 'object' &&
              Array.isArray((raw as { packages?: Package[] }).packages)
            ? (raw as { packages: Package[] }).packages
            : [];
        return of(this.processPackages(rows));
      }),
      catchError(err => {
        this.logger.error('Failed to load packages by wash types', err, 'PackageService');
        return of([]);
      })
    );
  }

  /** Shop discounts for order-request / checkout tiles (`GET package/discounts`). */
  getDiscounts(options?: {
    limit?: number;
    is_active?: boolean;
    /** Prefer `normal` so per-order custom discounts never appear as tiles. */
    type?: 'normal' | 'custom';
    sort_by?: string;
    order_by?: 'asc' | 'desc';
    location_id?: string;
  }): Observable<ApiResponse<PackageDiscount[]>> {
    const params = new URLSearchParams();
    if (options?.limit != null) params.set('limit', String(options.limit));
    if (options?.is_active != null) params.set('is_active', String(options.is_active));
    if (options?.type) params.set('type', options.type);
    if (options?.sort_by) params.set('sort_by', options.sort_by);
    if (options?.order_by) params.set('order_by', options.order_by);
    if (options?.location_id != null && String(options.location_id).trim() !== '') {
      params.set('location_id', String(options.location_id).trim());
    }
    const qs = params.toString();
    const endpoint = qs ? `package/discounts?${qs}` : 'package/discounts';
    return this.apiService.get<PackageDiscount[]>(endpoint);
  }

  /**
   * Audience bucket for the selected plate (`GET order/discount-eligibility`).
   *
   * first_time / returning discounts cannot be filtered client-side without knowing
   * whether this plate has any prior order. svc-order answers from the same lookup it
   * enforces with, so the tiles and the API cannot disagree. Omit the plate when
   * unknown — that counts as first time.
   */
  getDiscountEligibility(options?: { licensePlate?: string | null }): Observable<
    ApiResponse<{
      license_plate: string | null;
      customer_history: string;
    }>
  > {
    const params = new URLSearchParams();
    const plate = String(options?.licensePlate ?? '').trim();
    if (plate) {
      params.set('license_plate', plate);
    }
    const qs = params.toString();
    const endpoint = qs ? `order/discount-eligibility?${qs}` : 'order/discount-eligibility';
    return this.apiService.get<{
      license_plate: string | null;
      customer_history: string;
    }>(endpoint);
  }

  /**
   * Clear packages cache - useful when user changes
   */
  clearPackagesCache(): void {
    this.packagesSubject.next([]);
    this.packagesLoadingSubject.next(false);
    this.clearCarWashPackagesCache();
  }

  /**
   * Clear car wash packages cache - useful when packages are added/updated/deleted
   */
  clearCarWashPackagesCache(): void {
    this.carWashPackagesCache = null;
    this.carWashPackagesCachePromise = null;
    this.carWashPackagesForStaffCache = null;
    this.carWashPackagesForStaffCachePromise = null;
    this.carWashPackagesCacheClearedSubject.next();
    this.logger.debug('Cleared car wash packages cache', {}, 'PackageService');
  }

  /**
   * Processes packages to add computed properties
   * @param packages - Raw packages from API
   * @returns Processed packages with additional properties
   */
  private processPackages(packages: Package[]): Package[] {
    return packages.map((pkg: Package) => ({
      ...pkg,
      locations_str: pkg.locations?.map(l => l.name).join(', ') || '',
      // matTooltip is plain text; use newlines (with pre-line CSS), not <br>
      locations_tooltip: pkg.locations?.map(l => `• ${l.name}`).join('\n') || '',
      features_tooltip: pkg.features?.map(f => `• ${f}`).join('\n') || '',
      status_str: pkg.package_status ? 'Active' : 'Inactive',
    }));
  }
}
